import * as R from 'ramda';
import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';

import * as api from 'apis/restaurant-api/merchant-admin/menu2/api';
import { Category, Menu } from 'apis/restaurant-api/merchant-admin/menu2/types';

import { getItemCategory } from '../items/utils';
import useMenuData from '../useMenuData';
import { flattenCategories } from '../utils';

type ItemOrigin = {
  categoryId: string;
  position: number;
};

const useAsyncValue = <T>(value: T) => {
  const ref = useRef<T>(value);
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref;
};

const useItemCategoryChange = () => {
  const intl = useIntl();
  const { categories, menu, updateMenuData } = useMenuData();

  if (!menu) {
    throw new Error('Menu not found, this should not happen!');
  }

  const getItemOrigin = (itemId: string): ItemOrigin | null => {
    const originCategory = getItemCategory(categories, itemId);
    if (!originCategory) {
      return null;
    }
    const position = originCategory.item_bindings.findIndex(binding => binding.item_id === itemId);
    if (position === -1) {
      throw new Error('No binding for item found');
    }
    return { categoryId: originCategory.id, position };
  };

  const moveItemToCategory = async (targetCategoryId: string, itemId: string): Promise<Menu> => {
    try {
      return await api.createItemBinding(menu.id, targetCategoryId, { item_id: itemId });
    } catch (_) {
      throw new Error(intl.formatMessage({ id: 'menu.moveItemBackToCategoryFailed' }));
    }
  };

  const moveItemToPosition = async (category: Category, position: number, itemId: string): Promise<Menu> => {
    const newItemBindingIndex = category.item_bindings.findIndex(binding => binding.item_id === itemId);
    if (newItemBindingIndex === -1) {
      throw new Error('No binding for item found in updated menu');
    }
    const itemBindingForItem = category.item_bindings[newItemBindingIndex];
    const reorderedItemBindings = R.insert(
      position,
      itemBindingForItem,
      R.remove(newItemBindingIndex, 1, category.item_bindings),
    );
    try {
      return await api.updateItemBindingsOrder(
        menu.id,
        category.id,
        reorderedItemBindings.map(binding => binding.id),
      );
    } catch (_) {
      throw new Error(intl.formatMessage({ id: 'menu.moveItemBackToCategoryPositionFailed' }));
    }
  };

  const removeItemFromCategory = async (itemId: string): Promise<Menu> => {
    const itemCategory = getItemCategory(categories, itemId);
    if (!itemCategory) {
      throw new Error('Category not found for item');
    }
    const itemBinding = itemCategory.item_bindings.find(binding => binding.item_id === itemId);
    if (!itemBinding) {
      throw new Error('No binding for item found in updated menu');
    }
    return await api.deleteItemBinding(menu.id, itemCategory.id, itemBinding.id);
  };

  const undoItemCategoryChange = useAsyncValue(async (itemOrigin: ItemOrigin | null, itemId: string) => {
    if (itemOrigin) {
      let updatedMenu: Menu | undefined;
      try {
        updatedMenu = await moveItemToCategory(itemOrigin.categoryId, itemId);
        const newCategory = flattenCategories(updatedMenu.categories).find(
          category => category.id === itemOrigin.categoryId,
        );
        if (!newCategory) {
          throw new Error('Category not found for item');
        }
        updatedMenu = await moveItemToPosition(newCategory, itemOrigin.position, itemId);
      } finally {
        if (updatedMenu) {
          updateMenuData({ actionType: 'updateMenu', payload: updatedMenu });
        }
      }
    } else {
      const updatedMenu = await removeItemFromCategory(itemId);
      updateMenuData({ actionType: 'updateMenu', payload: updatedMenu });
    }
  });

  /**
   * @param targetCategoryId Removes from category if null
   * @param itemId
   * @returns An async function to undo the change
   */
  const changeItemCategory = async (targetCategoryId: string | null, itemId: string) => {
    const itemOrigin = getItemOrigin(itemId);
    let updatedMenu: Menu;
    if (targetCategoryId === null) {
      if (!itemOrigin) {
        throw new Error('Cannot remove item from category, because it does not belong to one');
      }
      updatedMenu = await removeItemFromCategory(itemId);
    } else {
      if (itemOrigin && itemOrigin.categoryId === targetCategoryId) {
        throw new Error('Item already belongs to the category');
      }
      updatedMenu = await api.createItemBinding(menu.id, targetCategoryId, { item_id: itemId });
    }
    updateMenuData({ actionType: 'updateMenu', payload: updatedMenu });
    const undo = () => undoItemCategoryChange.current(itemOrigin, itemId);
    return undo;
  };

  return changeItemCategory;
};

export default useItemCategoryChange;
